import { Slot } from '@radix-ui/react-slot';
import {
  type BorderRadius,
  type Heights,
  type Shadows,
  type Strokes,
  type ThemeColors,
  type Widths,
} from '@smart/web-foundations';
import classNames from 'classnames';
import React from 'react';
import type { ExtendWithCommonProps } from '../../types';
import {
  type Display,
  type Margin,
  type Padding,
  type ResponsiveProperties,
} from '../../types';
import { joinClasses, responsiveClasses, spacingClasses } from '../../utils';
import scss from './layout.module.scss';

export type LayoutProps = ExtendWithCommonProps<{
  id?: string;
  /** Component's height. */
  height?: ResponsiveProperties<Heights>;
  /** Component's width. */
  width?: ResponsiveProperties<Widths>;
  /** Component's display property. */
  display?: ResponsiveProperties<Display>;

  /** Component's padding. */
  padding?: Padding;
  /** Component's margin. */
  margin?: Margin;

  /** Component's background color. */
  backgroundColor?: ThemeColors | 'inherit';
  /** Component's text color. */
  color?: ThemeColors | 'inherit';

  /** Component's border width. */
  borderWidth?: Strokes;
  /** Component's border color. */
  borderColor?: ThemeColors;
  /** Component's border radius. */
  borderRadius?: BorderRadius;

  /** Component's box shadow. */
  boxShadow?: Shadows;

  asChild?: boolean;
  /** Component's children. */
  children?: React.ReactNode;
}>;

/**
 * Layout comonent, use to create or style an element with layout properties.
 * Use `asChild` to apply styles to the first single child.
 * @link https://prod.web-components.srv.smart.com/?path=/docs/layout-layout--docs
 */
export const Layout = React.forwardRef<HTMLDivElement, LayoutProps>(
  (
    {
      children,
      className,
      asChild,
      width,
      height,
      display,
      padding,
      margin,
      backgroundColor,
      color,
      borderWidth,
      borderColor,
      borderRadius,
      ...props
    }: LayoutProps,
    ref,
  ) => {
    const classes = classNames(
      joinClasses(responsiveClasses('height', height), scss),
      joinClasses(responsiveClasses('width', width), scss),
      joinClasses(responsiveClasses('display', display), scss),
      joinClasses(spacingClasses('margin', margin), scss),
      joinClasses(spacingClasses('padding', padding), scss),
      {
        [scss[`background-color-${backgroundColor}`]]: backgroundColor,
        [scss[`color-${color}`]]: color,
        [scss[`border-width-${borderWidth}`]]: borderWidth,
        [scss[`border-color-${borderColor}`]]: borderColor,
        [scss[`border-radius-${borderRadius}`]]: borderRadius,
        [scss.border]: borderWidth,
        [scss[`box-shadow-${props.boxShadow}`]]: props.boxShadow,
      },
      className,
    );

    const Tag = asChild ? Slot : 'div';

    return (
      <Tag ref={ref} className={classes} {...props}>
        {children}
      </Tag>
    );
  },
);

Layout.displayName = 'Layout';
